import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle, XCircle, ShieldWarning, CircleNotch } from "@phosphor-icons/react";

/**
 * PasswordStrengthMeter
 *
 * Live client-side strength feedback that mirrors the server gates
 * (validate_password_strength + HIBP breach-check). Renders a 4-segment
 * color bar + per-rule checklist + an HIBP breach warning that polls
 * https://api.pwnedpasswords.com via the k-anonymity range API.
 *
 * Props:
 *   password         current value of the password input
 *   onValidityChange (optional) called whenever the overall "ok to submit"
 *                    state flips. Parent can use this to gate the submit
 *                    button. Receives a boolean.
 *
 * Privacy:
 *   - SHA-1 is computed in-browser via Web Crypto (subtle.digest).
 *   - Only the first 5 hex chars of the SHA-1 hash are sent to HIBP.
 *   - The remaining 35 chars are compared locally against the HIBP response.
 *   - Zero plaintext + only 5 of 40 hash chars ever leave the browser.
 *
 * Outage / debounce:
 *   - 500ms debounce so HIBP isn't hammered while the user types.
 *   - Network errors are silent (fail-open) — the server still gates on
 *     submit, so a temporary HIBP outage doesn't trap the user.
 */

const BANNED_PASSWORDS = new Set([
  "password", "passw0rd", "p@ssw0rd", "qwerty", "qwerty123", "letmein",
  "welcome", "welcome1", "admin", "admin123", "iloveyou", "monkey",
  "monkey12", "111111", "111111111", "11111111", "00000000",
  "1234", "12345", "123456", "1234567", "12345678", "123456789",
  "1234567890", "abc123", "abcdef12", "asdfghjk", "qwertyuiop",
  "1q2w3e4r", "zaq12wsx", "1qaz2wsx", "trustno1", "dragon", "dragon!1",
  "baseball", "football", "starwars", "letmein!", "qazwsx",
]);

async function sha1Hex(text) {
  const buf = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest("SHA-1", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();
}

async function hibpRangeCount(password) {
  if (!password || password.length < 8) return null;
  const hash = await sha1Hex(password);
  const prefix = hash.slice(0, 5);
  const suffix = hash.slice(5);
  const r = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`, {
    headers: { "Add-Padding": "true" },
  });
  if (!r.ok) throw new Error(`HIBP ${r.status}`);
  const body = await r.text();
  for (const line of body.split(/\r?\n/)) {
    const [suf, cnt] = line.split(":");
    if (suf && suf.trim().toUpperCase() === suffix) {
      return parseInt(cnt, 10) || 0;
    }
  }
  return 0;
}

function classifyStrength(pw) {
  const long = pw.length >= 8;
  const longer = pw.length >= 12;
  const has_lower = /[a-z]/.test(pw);
  const has_upper = /[A-Z]/.test(pw);
  const has_digit = /\d/.test(pw);
  const has_symbol = /[^a-zA-Z0-9]/.test(pw);
  const classes = [has_lower, has_upper, has_digit, has_symbol].filter(Boolean).length;
  const banned = BANNED_PASSWORDS.has(pw.toLowerCase());
  const tooShort = !long;
  const tooLong = pw.length > 128;
  const not_enough_mix = classes < 3;
  const ok = long && !tooLong && classes >= 3 && !banned;

  let score = 0;
  if (long) score++;
  if (classes >= 3) score++;
  if (longer && classes >= 4) score++;
  if (longer && pw.length >= 16) score++;
  if (banned) score = 0;
  if (tooShort) score = 0;
  // 0=empty/banned, 1=weak, 2=fair, 3=strong, 4=excellent

  return {
    ok,
    score,
    rules: { long, has_lower, has_upper, has_digit, has_symbol, banned, tooLong, not_enough_mix },
  };
}

function strengthLabel(score) {
  return ["", "Weak", "Fair", "Strong", "Excellent"][score] || "";
}

function strengthColor(score, breachCount) {
  if (breachCount > 0) return "bg-rose-500";
  if (score <= 1) return "bg-rose-500";
  if (score === 2) return "bg-amber-500";
  if (score === 3) return "bg-emerald-500";
  return "bg-emerald-600";
}

function breachBucket(count) {
  if (count >= 100000) return "millions of";
  if (count >= 1000) return "tens of thousands of";
  if (count >= 100) return "hundreds of";
  if (count > 1) return "multiple";
  return "a known";
}

const RuleItem = ({ ok, label, testid }) => (
  <li className="flex items-center gap-2" data-testid={testid}>
    {ok ? (
      <CheckCircle size={14} weight="fill" className="text-emerald-600" />
    ) : (
      <XCircle size={14} weight="regular" className="text-stone-300" />
    )}
    <span className={`text-[12px] ${ok ? "text-[#184A30]" : "text-[#4A5C50]"}`}>{label}</span>
  </li>
);

export default function PasswordStrengthMeter({ password, onValidityChange }) {
  const [breachCount, setBreachCount] = useState(null);
  const [checking, setChecking] = useState(false);
  const cls = useMemo(() => classifyStrength(password || ""), [password]);

  // Debounced HIBP check
  useEffect(() => {
    setBreachCount(null);
    if (!password || password.length < 8 || cls.rules.banned || !cls.ok) {
      setChecking(false);
      return;
    }
    let cancelled = false;
    setChecking(true);
    const id = setTimeout(async () => {
      try {
        const count = await hibpRangeCount(password);
        if (!cancelled) setBreachCount(count);
      } catch {
        if (!cancelled) setBreachCount(null); // fail-open silently
      } finally {
        if (!cancelled) setChecking(false);
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(id);
      setChecking(false);
    };
  }, [password, cls.ok, cls.rules.banned]);

  // Bubble validity up so parents can gate submit
  const isValid = cls.ok && (breachCount === null || breachCount === 0);
  useEffect(() => {
    if (typeof onValidityChange === "function") onValidityChange(isValid);
  }, [isValid, onValidityChange]);

  if (!password) return null;

  return (
    <div className="mt-3 space-y-3" data-testid="password-strength-meter">
      {/* 4-segment bar */}
      <div className="flex gap-1.5" aria-hidden>
        {[1, 2, 3, 4].map((seg) => {
          const filled = seg <= cls.score && breachCount !== 0 ? cls.score : seg <= cls.score ? cls.score : 0;
          const active = seg <= cls.score && (breachCount === null || breachCount === 0);
          return (
            <div
              key={seg}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                active ? strengthColor(cls.score, breachCount || 0) : "bg-stone-200"
              }`}
            />
          );
        })}
      </div>

      <div className="flex items-baseline justify-between">
        <span
          className={`text-[11px] tracking-[0.2em] uppercase font-semibold ${
            breachCount > 0
              ? "text-rose-700"
              : cls.score <= 1
              ? "text-rose-700"
              : cls.score === 2
              ? "text-amber-700"
              : "text-emerald-700"
          }`}
          data-testid="password-strength-label"
        >
          {breachCount > 0 ? "BREACHED" : strengthLabel(cls.score) || "—"}
        </span>
        {checking && (
          <span className="inline-flex items-center gap-1 text-[10px] text-[#4A5C50]">
            <CircleNotch size={11} className="animate-spin" /> checking breaches…
          </span>
        )}
      </div>

      {/* Breach warning */}
      {breachCount > 0 && (
        <div
          className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-xs text-rose-800 flex items-start gap-2"
          data-testid="password-breach-warning"
        >
          <ShieldWarning size={16} weight="fill" className="text-rose-600 shrink-0 mt-0.5" />
          <p>
            This password has been exposed in <strong>{breachBucket(breachCount)}</strong> data breaches.
            Please choose a unique one you haven't used elsewhere.
          </p>
        </div>
      )}

      {/* Rule checklist */}
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-2">
        <RuleItem ok={cls.rules.long} label="At least 8 characters" testid="rule-long" />
        <RuleItem ok={cls.rules.has_lower} label="Lowercase letter (a-z)" testid="rule-lower" />
        <RuleItem ok={cls.rules.has_upper} label="Uppercase letter (A-Z)" testid="rule-upper" />
        <RuleItem ok={cls.rules.has_digit} label="Digit (0-9)" testid="rule-digit" />
        <RuleItem ok={cls.rules.has_symbol} label="Symbol (!@#…)" testid="rule-symbol" />
        <RuleItem ok={!cls.rules.banned} label="Not on top-50 leaked list" testid="rule-not-banned" />
      </ul>
    </div>
  );
}
